import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation, Trans } from 'react-i18next';
import { Rocket, Check, Users, Award, Star, Gift, Settings, Flag, Info } from 'lucide-react';

import { AgentData_V10, AgentHubPageViewMode, CrowdfundingDetailsV10, CrowdfundingTierV10 } from '@dopomogai/types';
import { Card } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import {
  INNER_CARD_ANIMATE_RESTING,
  INNER_CARD_HOVER,
  INNER_CARD_TRANSITION_HOVER,
} from '../../shared/animationConstants';

// --- Sub-Components ---

interface TierCardProps {
  tier: CrowdfundingTierV10;
  baseFcuRate: number;
  onAction: (actionType: string, payload?: any) => void;
  t: (key: string, options?: any) => string;
}

const TierCard: React.FC<TierCardProps> = ({ tier, baseFcuRate, onAction, t }) => {
  const pranaiAmountFormatted = tier.pranaiAmount.toLocaleString();
  const usdEquivalentFormatted = tier.usdEquivalent.toLocaleString();
  
  // Correctly calculate all necessary values for display
  const baseFcus = Math.floor(tier.pranaiAmount / baseFcuRate);
  const bonusFcus = tier.bonusFcuAmount || 0;
  const totalFcus = baseFcus + bonusFcus;
  
  const totalFcusFormatted = totalFcus.toLocaleString();
  const baseFcusFormatted = baseFcus.toLocaleString();
  const bonusFcusFormatted = bonusFcus.toLocaleString();

  return (
    <motion.div
      animate={INNER_CARD_ANIMATE_RESTING}
      whileHover={INNER_CARD_HOVER}
      transition={INNER_CARD_TRANSITION_HOVER}
      className="bg-white border-2 border-black rounded-xl p-6 flex flex-col text-center h-full relative"
    >
      {tier.isPopular && (
        <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-amber-400 text-black px-3 py-1 rounded-full text-xs font-bold border-2 border-black shadow-sm flex items-center gap-1.5">
          <Star size={14} /> {t('crowdfundingSection.phase1.mostPopularBadge')}
        </div>
      )}
      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-amber-50 border-2 border-amber-200">
        {tier.id === 'bronze' && <Award size={32} className="text-amber-600" />}
        {tier.id === 'silver' && <Star size={32} className="text-amber-600" />}
        {tier.id === 'gold' && <Gift size={32} className="text-amber-600" />}
        {tier.id === 'platinum' && <Rocket size={32} className="text-amber-600" />}
      </div>
      <h4 className="text-xl font-bold text-gray-900">{t(tier.nameKey)}</h4>
      
      {/* UPDATED: USD is now the primary, larger display */}
      <p className="text-3xl font-black text-emerald-600 my-2">
         ${usdEquivalentFormatted}
      </p>
      <p className="text-sm text-gray-500 mb-5">({pranaiAmountFormatted} PRANAI)</p>
      
      <div className="flex-grow mb-6">
        <ul className="space-y-2.5 text-left text-sm text-gray-700 mb-4">
          {tier.perksKeys.map(perkKey => (
            <li key={perkKey} className="flex items-start gap-3">
              <Check size={18} className="text-green-500 mt-0.5 shrink-0" />
              <span>{t(perkKey)}</span>
            </li>
          ))}
        </ul>
        <div className="font-semibold text-sky-800 bg-sky-50 p-3 rounded-lg border-2 border-sky-200">
           {/* FIXED: This now correctly passes all variables to fix NaN and {{baseCount}} errors */}
           {bonusFcus > 0
              ? t('crowdfundingSection.tiers.fcuSecuredWithBonus', { 
                  count: totalFcusFormatted, 
                  baseCount: baseFcusFormatted, 
                  bonusCount: bonusFcusFormatted 
                })
              : t('crowdfundingSection.tiers.fcuSecured', { 
                  count: totalFcusFormatted 
                })
            }
        </div>
      </div>

      <Button
        textKey="crowdfundingSection.buttons.pledge"
        textOptions={{ amount: pranaiAmountFormatted }}
        variant="secondary"
        fullWidth
        onClick={() => onAction('TRIGGER_PLEDGE_CONFIRMATION', { tierId: tier.id })}
      />
    </motion.div>
  );
};

const CustomPledgeCard: React.FC<{ pledgeData: NonNullable<CrowdfundingDetailsV10['pledgePhaseData']>; onAction: Function; t: Function }> = ({ pledgeData, onAction, t }) => {
  const { minPranaiAmount, pranaiPerFcuRate } = pledgeData.customPledge;
  const [usdAmount, setUsdAmount] = useState(minPranaiAmount); // State is now USD-based

  // All calculations derive from the single source of truth: usdAmount
  const pranaiAmount = Math.floor(usdAmount); // Simplified 1:1 conversion for mock
  const securedFcus = useMemo(() => {
    if (pranaiAmount < minPranaiAmount) return 0;
    return Math.floor(pranaiAmount / pranaiPerFcuRate);
  }, [pranaiAmount, minPranaiAmount, pranaiPerFcuRate]);

  const securedFcusFormatted = securedFcus.toLocaleString();
  const pranaiAmountFormatted = pranaiAmount.toLocaleString();

  return (
    <motion.div
      animate={INNER_CARD_ANIMATE_RESTING}
      whileHover={INNER_CARD_HOVER}
      transition={INNER_CARD_TRANSITION_HOVER}
      className="bg-emerald-50 border-2 border-black rounded-xl p-6 flex flex-col text-center h-full"
    >
      <h4 className="text-xl font-bold text-gray-900">{t('crowdfundingSection.customPledge.title')}</h4>
      <p className="text-sm text-gray-600 my-2">{t('crowdfundingSection.customPledge.description')}</p>

      <div className="my-3 text-center">
        <span className="bg-emerald-200 text-emerald-800 text-sm font-semibold px-3 py-1.5 rounded-full border-2 border-emerald-300">
           {t('crowdfundingSection.customPledge.baseRateLabel', { rate: pranaiPerFcuRate })}
        </span>
      </div>

      {/* UPDATED: This entire section is now USD-first for a better UX */}
      <div className="my-4 flex-grow">
        <label htmlFor="custom-pledge-input" className="block text-sm font-medium text-gray-700 mb-1 text-left">
          {t('crowdfundingSection.customPledge.usdAmountLabel')}
        </label>
        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <span className="text-gray-500 text-2xl"> $</span>
          </div>
          <input
            id="custom-pledge-input"
            type="number"
            min={minPranaiAmount}
            step={10} // User-friendly step amount in USD
            value={usdAmount}
            onChange={(e) => setUsdAmount(Number(e.target.value))}
            className="w-full p-3 pl-10 text-2xl font-bold border-2 border-black rounded-lg text-center focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
        <div className="text-sm mt-2 text-gray-600 text-center font-medium">
          <span className="text-gray-800">{t('crowdfundingSection.customPledge.pranaiEquivalentLabel', { amount: pranaiAmountFormatted })}</span>
          <span className="mx-2">|</span>
          <span className="font-semibold text-sky-700">{t('crowdfundingSection.customPledge.securesFcusLabel', { count: securedFcusFormatted })}</span>
        </div>
      </div>
      
      <Button
        textKey="crowdfundingSection.customPledge.buttonText"
        variant="primary"
        fullWidth
        disabled={pranaiAmount < minPranaiAmount}
        onClick={() => onAction('TRIGGER_PLEDGE_CONFIRMATION', { pranaiAmount: pranaiAmount })}
      />
    </motion.div>
  );
};


const ProgressTracker: React.FC<{ pledged: number; goal?: number; supporters: number; t: Function }> = ({ pledged, goal, supporters, t }) => {
  const progressPercentage = goal && goal > 0 ? Math.min((pledged / goal) * 100, 100) : 0;
  
  const supportersFormatted = supporters.toLocaleString();
  const pledgedFormatted = pledged.toLocaleString();
  const goalFormatted = goal?.toLocaleString();

  return (
    <div className="bg-slate-50 border-2 border-black rounded-xl p-5">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-2 text-lg shrink-0">
          <Users size={24} className="text-sky-600" />
          <span className="font-bold text-gray-800">{supportersFormatted}</span>
          <span className="text-gray-600">{t('crowdfundingSection.phase1.supportersLabel')}</span>
        </div>

        {goal && (
          <div className="w-full flex-grow">
            <div className="flex justify-between text-sm font-medium text-gray-700 mb-1">
              <span>{t('crowdfundingSection.phase1.pledgedLabel')}</span>
              <span>{pledgedFormatted} / {goalFormatted}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-5 border-2 border-slate-300 p-0.5">
              <motion.div
                className="bg-emerald-500 h-full rounded-full"
                initial={{ width: '0%' }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


// --- Main Component ---
interface CrowdfundingSectionProps {
  agentData: AgentData_V10;
  viewMode: AgentHubPageViewMode;
  onAction: (actionType: string, payload?: any) => void;
  headerBgColor?: string;
  id?: string;
}

export const CrowdfundingSection = React.forwardRef<HTMLDivElement, CrowdfundingSectionProps>(({ 
   agentData, viewMode, onAction, headerBgColor, id }, ref) => {
    const { t } = useTranslation('agent-common');
    const isOwnerView = viewMode === 'owner_dashboard';
    const crowdfundingData = agentData.crowdfundingDetails;

    if (!crowdfundingData || !crowdfundingData.isActive) {
      if (isOwnerView) {
        return (
          <Card id={id} ref={ref} titleKey="crowdfundingSection.ownerTitle" icon={<Gift size={22} className="text-pink-600" />} headerBgColor={headerBgColor}>
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">This agent is not currently seeking community funding.</p>
              <Button text="Configure Funding Campaign" icon={<Settings size={16}/>} onClick={() => onAction('SETUP_CROWDFUNDING', {})} />
            </div>
          </Card>
        );
      }
      return null;
    }

    if (crowdfundingData.phase === 'PledgeForFuture' && crowdfundingData.pledgePhaseData) {
      const data = crowdfundingData.pledgePhaseData;
      const progressTrackerData = data.progressTracker;

      return (
        <Card 
          id={id} 
          ref={ref} 
          title={t(data.headlineKey)}
          icon={<Rocket size={22} className="text-fuchsia-600" />} 
          headerBgColor={headerBgColor}
        >
          <div className="space-y-12">
            
            <div className="text-center">
              <p className="text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
                {t(data.introductionKey, { 
                  agentName: agentData.name, 
                  developmentStage: agentData.developmentUpdatesSection?.currentDevelopmentStage || 'early stage' 
                })}
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
                <h3 className="text-xl font-semibold text-gray-800 text-center mb-4">{t(data.focusTitleKey)}</h3>
                <ul className="space-y-3">
                  {data.currentDevelopmentFocusGoals.map((goal, index) => (
                    <li key={index} className="flex items-center gap-3 bg-white p-3 rounded-lg border-2 border-slate-200 shadow-sm">
                      <Flag size={20} className="text-emerald-500 flex-shrink-0" />
                      <span className="text-gray-700">{goal}</span>
                    </li>
                  ))}
                </ul>
            </div>
            
            <ProgressTracker
              pledged={progressTrackerData.pledgedPranaiAmount}
              goal={progressTrackerData.pranaiGoalAmount}
              supporters={progressTrackerData.supporterCount}
              t={t}
            />

            <div className="bg-sky-50 border-2 border-sky-200/80 rounded-xl p-4 max-w-4xl mx-auto">
              <div className="flex items-start gap-3">
                <Info size={24} className="text-sky-600 mt-0.5 shrink-0" />
                <p className="text-sm text-gray-800 text-left">
                   <Trans
                    i18nKey={data.importantNoticeKey}
                    components={[
                      <a href={data.whitepaperUrl} className="font-semibold text-sky-700 hover:underline" target="_blank" rel="noopener noreferrer" />,
                      <a href={data.pledgeTermsUrl} className="font-semibold text-sky-700 hover:underline" target="_blank" rel="noopener noreferrer" />,
                    ]}
                  />
                </p>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">{t(data.backersTitleKey)}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 items-stretch">
                {data.contributionTiers.map(tier => 
                    <TierCard 
                        key={tier.id} 
                        tier={tier} 
                        onAction={onAction} 
                        t={t} 
                        baseFcuRate={data.customPledge.pranaiPerFcuRate} 
                    />
                )}
                <div className="md:col-span-2 xl:col-span-4">
                  <CustomPledgeCard pledgeData={data} onAction={onAction} t={t} />
                </div>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t-2 border-slate-100">
              <p className="text-xs text-gray-500 bg-slate-50 p-4 rounded-lg border-2 border-slate-200">
                <Trans 
                  i18nKey={data.legalDisclaimerKey} 
                  values={{ agentName: agentData.name }}
                  components={[
                     <a href={data.pledgeTermsUrl} className="font-semibold text-sky-600 hover:underline" target="_blank" rel="noopener noreferrer" />
                  ]}
                />
              </p>
            </div>
          </div>
        </Card>
      );
    }
    
    return null;
  }
);

CrowdfundingSection.displayName = "CrowdfundingSection";
